import { type ReactNode, useEffect, useRef, useState } from 'react';

import { Loader2 } from 'lucide-react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { tokenStorage } from '@api/lib/token-storage';
import { useAuthStore } from '@stores/auth-store';

import { useQuickLogin } from '@features/auth/hooks/use-workshop-auth';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminToken = searchParams.get('admin_access');

  // Only start in quick-login mode if adminToken is present AND no user is authenticated yet
  const [isProcessingQuickLogin, setIsProcessingQuickLogin] = useState(() =>
    Boolean(adminToken && !useAuthStore.getState().user),
  );
  const quickLoginMutation = useQuickLogin();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!adminToken || attemptedRef.current) return;
    attemptedRef.current = true;
    setIsProcessingQuickLogin(true);

    // 1. Clear old tokens to guarantee a fresh session
    tokenStorage.removeAccessToken();
    tokenStorage.removeRefreshToken();
    tokenStorage.removePermissions();
    sessionStorage.clear();

    // 2. Safety timeout: if request takes > 8 seconds, release spinner and redirect
    const timeoutTimer = setTimeout(() => {
      setIsProcessingQuickLogin(false);
      toast.error('Quick login timed out. Please try again.');
      navigate('/login', { replace: true });
    }, 8000);

    // 3. Trigger quick login
    quickLoginMutation.mutate(adminToken, {
      onSuccess: () => {
        clearTimeout(timeoutTimer);
        // Remove admin_access from search params cleanly
        setSearchParams(
          (prev) => {
            const updated = new URLSearchParams(prev);
            updated.delete('admin_access');
            return updated;
          },
          { replace: true },
        );
        setIsProcessingQuickLogin(false);
        navigate('/dashboard', { replace: true });
      },
      onError: (err) => {
        clearTimeout(timeoutTimer);
        setIsProcessingQuickLogin(false);
        toast.error(err.message || 'Quick login link is invalid or expired.');
        navigate('/login', { replace: true });
      },
    });

    return () => clearTimeout(timeoutTimer);
  }, [adminToken, quickLoginMutation, navigate, setSearchParams]);

  // If user is already set in store, render children immediately (do not block)
  if (user) {
    return <>{children}</>;
  }

  // If quick login is actively in flight and no user is logged in yet, show spinner
  if (adminToken && (isProcessingQuickLogin || quickLoginMutation.isPending)) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Signing in via admin access"
        style={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px',
          background: '#0f172a',
          color: '#f8fafc',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <Loader2 className="animate-spin" size={36} color="#3b82f6" />
        <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.02em' }}>
          Signing into Workshop ERP…
        </span>
      </div>
    );
  }

  // Session restore loading spinner (on normal page refresh)
  if (isLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Verifying session"
        style={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px',
          background: '#0f172a',
          color: '#f8fafc',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <Loader2 className="animate-spin" size={36} color="#3b82f6" />
        <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.02em' }}>
          Verifying session…
        </span>
      </div>
    );
  }

  // Unauthenticated user
  return <Navigate to="/login" state={{ from: location }} replace />;
}
